import React, { useEffect, useState } from "react";
import CheckoutModal from "./components/CheckoutModal";
import { useCategoryState } from "../hooks/category/useCategoryState";
import { useDebounce } from "../hooks/useDebounce";
import { useCategoryData } from "../hooks/useCategoryData";
import { useProductData } from "../hooks/useProductData";
import { useAppearance } from "../hooks/useAppearance";
import { useDiscountHandler } from "../hooks/category/useDiscountHandler";
import { useCartSave } from "../hooks/category/useCartSave";
import LoadingScreen from "../../../../../shared/components/LoadingScreen";
import CategorySection from "../CategorySection";
import ProductGrid from "../product-grid/ProductGrid";
import OrderSummary from "../order-summary/OrderSummary";
import { useCartManagement } from "../hooks/order-summary/useCartManagement";
import ProductCategories from "../ProductCategories";
import { Productbtn } from "../../../../../shared/services/interfacev2";
import { useOrderProcessing } from "../Payment/hooks/useOrderProcessing";
import { ToastContainer } from "react-toastify";
import { useDeliveryFeeHandler } from "../hooks/category/useDeliveryFeeHandler";
import { useUser } from "src/shared/context/UserContext";
import { notifyError, notifySuccess } from "src/assets/toastService";

function Category() {
  const { searchQuery, setSearchQuery, categoryID, setCategoryId, cartId, setCartId, modal, setModal, resetCategoryState } = useCategoryState();

  const { prvDelete } = useOrderProcessing();
  const filteredTerm = useDebounce(searchQuery, 800);
  const { buttonNames, loading: categoriesLoading } = useCategoryData();
  const [form] = useState({ refresh: false, isDeleted: false });
  const { selectedProducts, loading: productsLoading } = useProductData({ deleted: form.isDeleted, filteredTerm, categoryID });
  const {
    selectedProductslist,
    valueMap,
    totalQuantity,
    totalAmount,
    condition,
    disValue,
    setCondition,
    deliveryFee,
    grandTotal,
    setDeliveryFee,
    addProductToCart,
    removeProductFromCart,
    updateQuantity,
    resetCart,
    updateProductAmount,
    editedAmounts, // Import this
  } = useCartManagement();

  const { background } = useAppearance();
  const { userConfigration } = useUser();
  const [activeProductId, setActiveProductId] = React.useState<string | null>(null);

  useEffect(() => {
    if (prvDelete) {
      resetCart();
    }
  }, [prvDelete, resetCart]);

  const handleProductSelect = (product: Productbtn) => {
    addProductToCart(product);
    setActiveProductId(product._id.$oid);
  };

  const handleDiscountChange = useDiscountHandler(totalAmount, setCondition);
  
  const deliveryFeeHandler = useDeliveryFeeHandler(setDeliveryFee);

  const handleDeliveryFeeChange = deliveryFeeHandler

  // Handle amount editing with configuration-based permission
  const handleAmountEdit = (productId: string, newAmount: string) => {
    // Check if product editing is enabled in configuration
    if (!productEditEnabled) {
      notifyError("Product editing is not enabled. Please enable it in configuration settings.");
      return;
    }

    // Additional permission check if needed
    // if (!user?.permissions?.canEditOrderAmount) {
    //   notifyError("You don't have permission to edit order amounts");
    //   return;
    // }

    const numericAmount = parseFloat(newAmount);

    // Validate amount
    if (isNaN(numericAmount) || numericAmount <= 0) {
      notifyError("Please enter a valid amount greater than 0");
      return;
    }

    // Validate max amount (optional - adjust as needed)
    if (numericAmount > 999999.99) {
      notifyError("Amount exceeds maximum allowed value");
      return;
    }

    // Find the product to verify it exists
    const productExists = selectedProductslist.some((p) => p._id.$oid === productId);
    if (!productExists) {
      notifyError("Product not found in cart");
      return;
    }

    // Update the product amount
    updateProductAmount(productId, numericAmount.toFixed(2));
    notifySuccess("Product amount updated successfully");
  };

  const Savecart = useCartSave({
    condition,
    disValue,
    totalAmount,
    totalQuantity,
    deliveryFee,
    selectedProductslist,
    valueMap,
    setCartId,
  });

  const loading = categoriesLoading || productsLoading;

  const toBoolean = (value: any) => value === true || value === "true";
  const deliveryFeeEnabled = toBoolean(userConfigration?.deliveryFeeEnabled);
  const productEditEnabled = toBoolean(userConfigration?.productEditEnabled); // Get the configuration setting

  const backHome = (data: any) => {
    if (data === "false") setModal(!modal);
    if (data === "done") resetAll();
  };

  const handleDone = () => {
    resetAll();
    setModal(false);
  };

  const Checkout = () => {
    Savecart("close");
    if (totalAmount) {
      setTimeout(() => setModal(!modal), 100);
    }
  };

  const resetAll = () => {
    resetCategoryState();
    resetCart();
    setActiveProductId(null);
  };

  return (
    <div className="w-full h-[92vh] overflow-hidden bg-white">
      {loading && <LoadingScreen />}

      <div className="hidden lg:flex flex-row h-full overflow-auto bg-white">
        <div className="lg:w-4/6 w-full p-2">
          <CategorySection searchQuery={searchQuery} onSearchChange={setSearchQuery} />
          <div className="thin-scrollbar flex flex-row gap-2 w-full mt-1 h-[84vh] overflow-auto">
            <ProductCategories categories={buttonNames} selectedCategoryId={categoryID} backgroundColor={background} onCategorySelect={setCategoryId} />
            <ProductGrid products={selectedProducts} categorySelected={!!categoryID} onProductSelect={handleProductSelect} selectedProduct={activeProductId} />
          </div>
        </div>
        <OrderSummary
          selectedProducts={selectedProductslist}
          valueMap={valueMap}
          totalQuantity={totalQuantity}
          totalAmount={totalAmount}
          condition={condition}
          disValue={disValue}
          onQuantityChange={updateQuantity}
          onProductRemove={(id, amount) => {
            removeProductFromCart(id, amount);
            if (activeProductId === id) {
              setActiveProductId(null);
            }
          }}
          onDiscountChange={handleDiscountChange}
          onCheckout={Checkout}
          onNewOrder={resetAll}
          onDeliveryFeeChange={handleDeliveryFeeChange}
          deliveryFee={deliveryFee}
          grandTotal={grandTotal}
          deliveryFeeEnabled={deliveryFeeEnabled}
          onAmountEdit={handleAmountEdit}
          isAmountEditable={productEditEnabled}
          editedAmounts={editedAmounts} // Pass this
        />
      </div>

      {/* Mobile layout */}
      <div className="flex flex-col lg:hidden h-full overflow-hidden">
        <div className="flex-1 overflow-y-auto p-2 space-y-4">
          <CategorySection searchQuery={searchQuery} onSearchChange={setSearchQuery} />
          <div className="flex gap-2">
            <div className="w-[90px] shrink-0">
              <ProductCategories categories={buttonNames} selectedCategoryId={categoryID} backgroundColor={background} onCategorySelect={setCategoryId} />
            </div>
            <div className="flex-1 overflow-x-auto">
              <ProductGrid products={selectedProducts} categorySelected={!!categoryID} onProductSelect={handleProductSelect} selectedProduct={activeProductId} />
            </div>
          </div>
          <OrderSummary
            selectedProducts={selectedProductslist}
            valueMap={valueMap}
            totalQuantity={totalQuantity}
            totalAmount={totalAmount}
            condition={condition}
            disValue={disValue}
            onQuantityChange={updateQuantity}
            onProductRemove={(id, amount) => {
              removeProductFromCart(id, amount);
              if (activeProductId === id) {
                setActiveProductId(null);
              }
            }}
            onDiscountChange={handleDiscountChange}
            onCheckout={Checkout}
            onNewOrder={resetAll}
            onDeliveryFeeChange={handleDeliveryFeeChange}
            deliveryFee={deliveryFee}
            grandTotal={grandTotal}
            deliveryFeeEnabled={deliveryFeeEnabled}
            onAmountEdit={handleAmountEdit}
            isAmountEditable={productEditEnabled}
          />
        </div>
      </div>

      <CheckoutModal modal={modal} cartId={cartId} backHome={backHome} onDone={handleDone} editedAmounts={editedAmounts} />
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick pauseOnHover />
    </div>
  );
}

export default Category;
